/**
 *
 * UserSelector
 *
 */

import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { FormattedMessage } from 'react-intl';
import { Alert, Form, ListGroup } from 'react-bootstrap';
import { useApi2, useApi2call } from 'containers/Api2/index.js';
import { PARENT_ROLE } from 'containers/User/constants.js';
import UserAvatar from 'components/user/UserAvatar/index.jsx';
import InfiniteScroll from 'components/interaction/InfiniteScroll/index.jsx';
import { parseUrl, serialize } from 'utils/urlUtils.js';
import { idValues } from 'utils/arrays.js';
import { getIdNumber } from 'utils/userIdRole.js';
import fixedBottomStyle from 'utils/fixedBottomStyle.js';
import Search from '../Search/index.jsx';
import messages from './messages.js';
import useSelectUsers from './useSelectUsers.js';

const PAGESIZE = 20;
const MARGIN_BOTTOM = 120;

const Wrapper = styled(ListGroup)`
  input[type="search"] {
    width: 12em;
  }
  .list-group-item:last-child {
    border-bottom: none;
  }
`;

function defaultShowUser(user, attributes) {
  const label = <UserAvatar user={user} />;
  return <Form.Check type="checkbox" id={user.id} {...attributes} label={label} />;
}

function UserSelector(props) {
  const {
    roleType, value, onChange, fetchOptions, exclusions = [], showUser = defaultShowUser, clientSearch, control, children, inboxId,
  } = props;

  const [search, setSearch] = useState('');
  const { handleCheck, isIncluded } = useSelectUsers(value, onChange);
  const lastRoleType = useRef(null);
  const lastStartUrl = useRef(null);
  const { url, typeField } = roleType?.fetchOptions ?? fetchOptions ?? {};
  const doClientSearch = roleType?.clientSearch ?? clientSearch;
  const hasRoleType = Boolean(typeField && roleType);
  const users = useApi2(getOptions());

  function applyNextUrl(nextUrl) {
    if (!nextUrl) {
      return false;
    }
    const { searchObject } = parseUrl(nextUrl);
    const sameType = lastRoleType?.current === roleType;
    lastRoleType.current = roleType;
    const sameSearch = !doClientSearch && searchObject && searchObject.search === search;
    return sameSearch && sameType;
  }

  function getOptions(nextUrl) {
    if (applyNextUrl(nextUrl)) {
      return nextUrl;
    }
    const params = {
      offset: 0,
      limit: doClientSearch ? 9999 : PAGESIZE,
    };
    if (search && !doClientSearch) {
      params.q = search;
    }
    if (inboxId) {
      params.inboxId = inboxId;
    }
    if (hasRoleType) {
      params[typeField] = roleType.id;
      if (roleType.id === PARENT_ROLE) {
        params.orderByParents = true;
      }
    }
    const sep = url.includes('?') ? '&' : '?';
    const startUrl = `${url}${sep}${serialize(params)}`;
    const reload = startUrl !== lastStartUrl.current;
    const baseSelector = `userselector/${url}`;
    const result = {
      ...fetchOptions,
      url: reload ? startUrl : undefined,
      selector: hasRoleType ? `${baseSelector}/${roleType.name}` : baseSelector,
      withPromise: true,
    };
    return result;
  }
  const { loading, hasMore, nextUrl, list, loadMore } = users;
  const api2call = useApi2call();

  useEffect(() => {
    const options = getOptions(nextUrl);
    if (options.url) {
      api2call(options).then(() => {
        lastStartUrl.current = options.url;
      });
    }
  }, [search, roleType]); // eslint-disable-line react-hooks/exhaustive-deps

  const userList = idValues(users);
  const searchRegex = doClientSearch && new RegExp(search, 'i');
  const filteredList = (search && doClientSearch) ? userList.filter((o) => searchRegex.test(o.name || o.accountName || o.displayName)) : userList;
  function renderList() {
    if (filteredList.length === 0) {
      return loading ? null : <Alert variant="info" className="m-4"><FormattedMessage {...messages.noUsers} /></Alert>;
    }
    return filteredList.map((user) => (
      <ListGroup.Item key={user.id} className="px-4 ms-2">
        {showUser(user, { checked: isIncluded(user), onChange: handleCheck(user), disabled: exclusions.includes(getIdNumber(user)) })}
      </ListGroup.Item>
    ));
  }
  return (
    <Wrapper className="mb-0">
      {children}
      <ListGroup.Item className="px-4 py-3 d-flex justify-content-between">
        <Search value={search} onChange={setSearch} delay={500} autoFocus />
        {typeof control === 'function' ? control(users) : control}
      </ListGroup.Item>
      {list && (
        <InfiniteScroll
          label={roleType?.name}
          hasMore={hasMore}
          loadMore={loadMore}
          loading={loading}
          getStyle={(elem) => fixedBottomStyle(MARGIN_BOTTOM, elem)}
        >
          {renderList()}
        </InfiniteScroll>
      )}
    </Wrapper>
  );
}

UserSelector.propTypes = {
  value: PropTypes.array.isRequired,
  roleType: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  fetchOptions: PropTypes.object,
  inboxId: PropTypes.string,
  exclusions: PropTypes.array,
  clientSearch: PropTypes.bool,
  children: PropTypes.node,
  showUser: PropTypes.func,
  control: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),
};

export default UserSelector;
